import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";

interface RecaptchaProps {
  siteKey: string;
  onVerify: (token: string) => void;
  onExpired?: () => void;
  onError?: () => void;
  theme?: "light" | "dark";
  size?: "normal" | "compact" | "invisible";
  className?: string;
  language?: string;
}

export interface RecaptchaRef {
  reset: () => void;
  getResponse: () => string;
}

declare global {
  interface Window {
    grecaptcha: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
          theme?: "light" | "dark";
          size?: "normal" | "compact" | "invisible";
        }
      ) => number;
      reset: (widgetId: number) => void;
      getResponse: (widgetId: number) => string;
    };
  }
}

const Recaptcha = forwardRef<RecaptchaRef, RecaptchaProps>(
  (
    {
      siteKey,
      onVerify,
      onExpired,
      onError,
      theme = "light",
      size = "normal",
      className = "",
      language = "he",
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<number | null>(null);
    const hasRenderedRef = useRef(false);

    useEffect(() => {
      if (hasRenderedRef.current) return;

      const waitForGrecaptcha = (
        onReady: () => void,
        maxAttempts: number,
        getAttempts: () => number,
        incrementAttempts: () => void
      ) => {
        incrementAttempts();
        if (
          window.grecaptcha &&
          typeof window.grecaptcha.render === "function" &&
          !hasRenderedRef.current
        ) {
          onReady();
        } else if (!hasRenderedRef.current && getAttempts() < maxAttempts) {
          setTimeout(() =>
            waitForGrecaptcha(onReady, maxAttempts, getAttempts, incrementAttempts)
          , 100);
        }
      };

      if (!window.grecaptcha) {
        const script = document.createElement("script");
        script.src = `https://www.google.com/recaptcha/api.js?hl=${language}`;
        script.async = true;
        script.defer = true;

        script.onload = () => {
          let attempts = 0;
          const maxAttempts = 50;
          waitForGrecaptcha(
            renderRecaptcha,
            maxAttempts,
            () => attempts,
            () => { attempts++; }
          );
        };

        document.head.appendChild(script);
      } else if (
        typeof window.grecaptcha.render === "function" &&
        !hasRenderedRef.current
      ) {
        renderRecaptcha();
      } else if (!hasRenderedRef.current) {
        let attempts = 0;
        const maxAttempts = 50;
        waitForGrecaptcha(
          renderRecaptcha,
          maxAttempts,
          () => attempts,
          () => { attempts++; }
        );
      }

      return () => {
        widgetIdRef.current = null;
        hasRenderedRef.current = false;
      };
    }, []);

    const renderRecaptcha = () => {
      if (
        !containerRef.current ||
        !window.grecaptcha ||
        hasRenderedRef.current
      ) {
        return;
      }

      if (typeof window.grecaptcha.render !== "function") {
        return;
      }

      try {
        widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token: string) => {
            onVerify(token);
          },
          "expired-callback": () => {
            onExpired?.();
          },
          "error-callback": () => {
            onError?.();
          },
          theme,
          size,
        });
        hasRenderedRef.current = true;
      } catch {
        hasRenderedRef.current = false;
        widgetIdRef.current = null;
      }
    };

    const reset = () => {
      if (widgetIdRef.current !== null && window.grecaptcha) {
        try {
          setTimeout(() => {
            try {
              if (window.grecaptcha && widgetIdRef.current !== null) {
                window.grecaptcha.reset(widgetIdRef.current);
              }
            } catch {
              // ignore reset errors
            }
          }, 100);
          hasRenderedRef.current = false;
        } catch {
          hasRenderedRef.current = false;
        }
      }
    };

    const getResponse = (): string => {
      if (widgetIdRef.current !== null && window.grecaptcha) {
        try {
          return window.grecaptcha.getResponse(widgetIdRef.current);
        } catch {
          return "";
        }
      }
      return "";
    };

    useImperativeHandle(
      ref,
      () => ({
        reset,
        getResponse,
      }),
      []
    );

    return (
      <div
        ref={containerRef}
        className={`recaptcha-container ${className}`}
        data-testid="recaptcha-container"
      />
    );
  }
);

Recaptcha.displayName = "Recaptcha";

export default Recaptcha;
